import {
  INTERPOLATION_BUFFER_SIZE,
  INTERPOLATION_DELAY_MS,
  type NetEntity,
  type NetPlayer,
} from '@md/shared';

/**
 * Interpolation du partenaire et des entités (spec §5.2).
 *
 * Le client affiche le monde distant avec 100 ms de retard : il dispose alors
 * toujours de deux snapshots encadrant l'instant de rendu, et peut interpoler
 * au lieu d'extrapoler. Résultat : un mouvement fluide et **sans dépassement**,
 * là où une extrapolation ferait tressauter le robot à chaque correction.
 *
 * Module pur, sans DOM ni réseau : testé en Vitest.
 */

export interface NetFrame {
  readonly tick: number;
  /** Horodatage de RÉCEPTION, en millisecondes (`performance.now()`). */
  readonly receivedAt: number;
  readonly players: readonly [NetPlayer, NetPlayer];
  /** Liste COMPLÈTE des entités (delta déjà fusionné). */
  readonly entities: readonly NetEntity[];
}

/** Position/vitesse interpolées d'un corps. */
export interface InterpolatedBody {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
}

export interface InterpolationBuffer {
  push(frame: NetFrame): void;
  /** Trame de rendu à l'instant `nowMs`, ou `null` si le tampon est vide. */
  sample(nowMs: number, delayMs?: number): NetFrame | null;
  latest(): NetFrame | null;
  size(): number;
  clear(): void;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpPlayer(a: NetPlayer, b: NetPlayer, t: number): NetPlayer {
  // Les grandeurs CONTINUES sont interpolées ; les grandeurs discrètes
  // (drapeaux, mort, accroche…) sont celles de la trame de gauche, pour ne
  // jamais afficher un état que le joueur n'a pas encore atteint.
  return {
    ...a,
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    vx: lerp(a.vx, b.vx, t),
    vy: lerp(a.vy, b.vy, t),
    energy: lerp(a.energy, b.energy, t),
  };
}

function lerpEntities(
  a: readonly NetEntity[],
  b: readonly NetEntity[],
  t: number,
): readonly NetEntity[] {
  if (b.length === 0) {
    return a;
  }
  const byId = new Map(b.map((entity) => [entity.id, entity]));
  return a.map((entity) => {
    const next = byId.get(entity.id);
    if (!next) {
      return entity;
    }
    return {
      ...entity,
      x: lerp(entity.x, next.x, t),
      y: lerp(entity.y, next.y, t),
      vx: lerp(entity.vx, next.vx, t),
      vy: lerp(entity.vy, next.vy, t),
    };
  });
}

export function createInterpolationBuffer(
  capacity = INTERPOLATION_BUFFER_SIZE,
): InterpolationBuffer {
  let frames: NetFrame[] = [];

  return {
    push(frame: NetFrame): void {
      // Un snapshot plus ancien que le dernier reçu (réseau désordonné) est
      // ignoré : il ferait reculer le rendu.
      const last = frames[frames.length - 1];
      if (last && frame.tick <= last.tick) {
        return;
      }
      frames.push(frame);
      if (frames.length > capacity) {
        frames = frames.slice(frames.length - capacity);
      }
    },

    sample(nowMs: number, delayMs = INTERPOLATION_DELAY_MS): NetFrame | null {
      if (frames.length === 0) {
        return null;
      }
      const renderTime = nowMs - delayMs;
      const first = frames[0];
      const last = frames[frames.length - 1];
      /* c8 ignore next 3 -- garanti non vide par le test ci-dessus */
      if (!first || !last) {
        return null;
      }
      if (renderTime <= first.receivedAt) {
        return first;
      }
      if (renderTime >= last.receivedAt) {
        // Aucun snapshot plus récent : on gèle sur le dernier connu plutôt que
        // d'extrapoler (le partenaire s'arrête net au lieu de traverser un mur).
        return last;
      }
      for (let i = frames.length - 1; i > 0; i -= 1) {
        const right = frames[i];
        const left = frames[i - 1];
        /* c8 ignore next 3 -- indices bornés par la boucle */
        if (!right || !left) {
          continue;
        }
        if (renderTime >= left.receivedAt && renderTime <= right.receivedAt) {
          const span = right.receivedAt - left.receivedAt;
          const t = span > 0 ? (renderTime - left.receivedAt) / span : 0;
          return {
            tick: left.tick,
            receivedAt: renderTime,
            players: [
              lerpPlayer(left.players[0], right.players[0], t),
              lerpPlayer(left.players[1], right.players[1], t),
            ],
            entities: lerpEntities(left.entities, right.entities, t),
          };
        }
      }
      return last;
    },

    latest(): NetFrame | null {
      return frames[frames.length - 1] ?? null;
    },

    size(): number {
      return frames.length;
    },

    clear(): void {
      frames = [];
    },
  };
}
